/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { getRequiredEnvVar } from "@shared/utils/validate-env";
import { FF_MOCK_AUTH } from "@shared/utils/feature-flag";

export { verifyToken, createJWTMock } from "./auth-jwt";

const USE_MOCK_AUTH = FF_MOCK_AUTH;

let REACT_APP_AUTHORITY_URL = '';
let REACT_APP_CLIENT_ID = '';

if (!USE_MOCK_AUTH) {
  REACT_APP_AUTHORITY_URL = getRequiredEnvVar('REACT_APP_AUTHORITY_URL');
  REACT_APP_CLIENT_ID = getRequiredEnvVar('REACT_APP_CLIENT_ID');
}

/**
 * getAuthToken function.
 *
 * @returns {*} Return value description
 */
const getUserDataKey = () =>
  `oidc:useruser:${REACT_APP_AUTHORITY_URL}:${REACT_APP_CLIENT_ID}`;

/**
 * Retrieves the current access token from localStorage.
 *
 * @returns The stored OIDC access token, or undefined if absent
 */
export const getAuthToken = () => {
  const user_datas = JSON.parse(window.localStorage.getItem(getUserDataKey()) || '{}');
  return user_datas?.access_token;
};

/**
 * Checks whether the stored access token is expired or will expire within
 * the given buffer window.
 *
 * @param bufferSeconds - Seconds before actual expiry to consider "near expiry"
 * @returns True when the token should be refreshed
 */
export function isTokenNearExpiry(bufferSeconds = 60): boolean {
  const raw = window.localStorage.getItem(getUserDataKey());
  if (!raw) return true;
  const data = JSON.parse(raw);
  const expiresAt = data?.expires_at;
  if (!expiresAt) return true;
  return Math.floor(Date.now() / 1000) >= expiresAt - bufferSeconds;
}

let refreshPromise: Promise<string | undefined> | null = null;

/**
 * Returns a valid (non-expired) access token. When the current token is near
 * expiry, transparently refreshes it via the OIDC service before returning.
 * Deduplicates concurrent refresh calls into a single inflight request.
 */
export async function getValidAuthToken(): Promise<string | undefined> {
  if (!isTokenNearExpiry()) {
    return getAuthToken();
  }
  if (refreshPromise) return refreshPromise;
  refreshPromise = doRefresh().finally(() => { refreshPromise = null; });
  return refreshPromise;
}

/**
 * Performs an OIDC access-token refresh via the lazily-imported oidcService.
 *
 * Falls back to the currently stored token when the OIDC service is unavailable
 * or when the refreshed user has no access token.
 *
 * @returns The refreshed access token, or the stored token as a fallback
 */
async function doRefresh(): Promise<string | undefined> {
  const { oidcService } = await import('@services/oidc/oidc');
  if (!oidcService) return getAuthToken();
  const user = await oidcService.refreshToken();
  return user?.access_token ?? getAuthToken();
}

/**
 * Forces an OIDC refresh-token grant, bypassing the local near-expiry
 * heuristic in {@link getValidAuthToken} and the stale-token fallback in
 * {@link doRefresh}.
 *
 * This is the recovery path for a confirmed server-side 401: the backend has
 * rejected the access token as expired, so the local `expires_at` heuristic
 * must not be trusted. A refresh-token grant is attempted unconditionally (as
 * long as a refresh token exists); on any failure this returns `undefined`
 * rather than silently handing back the stale token, so the caller can tell a
 * genuine, unrecoverable expiry apart from a token that merely looked valid
 * to the client. Concurrent callers share the same inflight refresh.
 *
 * @returns The freshly minted access token, or `undefined` when no refresh
 *          token is available or the grant failed.
 */
export async function forceRefreshToken(): Promise<string | undefined> {
  if (!getRefreshToken()) return undefined;
  if (refreshPromise) return refreshPromise;
  refreshPromise = forceRefresh().finally(() => { refreshPromise = null; });
  return refreshPromise;
}

/**
 * Executes the refresh-token grant without any stale-token fallback, returning
 * `undefined` when the OIDC service is unavailable or the refresh yields no
 * new access token.
 *
 * @returns The refreshed access token, or `undefined` on failure.
 */
async function forceRefresh(): Promise<string | undefined> {
  const { oidcService } = await import('@services/oidc/oidc');
  if (!oidcService) return undefined;
  const user = await oidcService.refreshToken();
  return user?.access_token ?? undefined;
}

/**
 * Retrieves the current refresh token from localStorage.
 *
 * @returns The stored OIDC refresh token, or undefined if absent
 */
export const getRefreshToken = (): string | undefined => {
  const user_datas = JSON.parse(window.localStorage.getItem(getUserDataKey()) || '{}');
  return user_datas?.refresh_token;
};

/**
 * Persists a refreshed access token and its expiration into localStorage.
 *
 * @param accessToken - The new access token
 * @param expiresIn - Token lifetime in seconds
 */
export const updateStoredTokens = (accessToken: string, expiresIn: number): void => {
  const key = getUserDataKey();
  const raw = window.localStorage.getItem(key);
  if (!raw) return;

  const user_datas = JSON.parse(raw);
  user_datas.access_token = accessToken;
  user_datas.expires_at = Math.floor(Date.now() / 1000) + expiresIn;
  window.localStorage.setItem(key, JSON.stringify(user_datas));
};

